import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, PackageSearch } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import { ProbeCommandPanel } from '../components/ProbeCommandPanel';
import { CustomsSkuPicker } from '../components/CustomsSkuPicker';
import { customsReasonCopy, toReviewReason } from '../components/assets/CustomsReason';

/**
 * SIL-695 — Customs: the holding area for hardware the inventory script found
 * but could not match to a known asset, plus the token that script carries and
 * the log of every reading it sent.
 *
 * Placement mirrors verification-links.tsx (Organization > Data & Integrations)
 * and its posture: the page is visible to any member, and the admin-only
 * actions — minting and revoking a token — are gated inside it rather than
 * behind a whole-page `adminOnly`. That matches clientAccess.ts, where the
 * read and triage routes are `orgRole: 'member'` and only the token routes are
 * `admin`.
 *
 * The two things this page has to communicate, beyond listing rows:
 *
 *   1. Everything here is SELF-REPORTED. It is what a machine said about
 *      itself through an unprivileged probe, not an attestation. Labelled on
 *      every row, because the difference decides how much weight a buyer's
 *      diligence can put on it.
 *   2. Where the declared and reported model disagree, BOTH are shown and
 *      neither has been overwritten. Machine-reported is usually right; the
 *      human decides.
 */

export const Route = createFileRoute('/customs')({
  component: CustomsPage,
});

// ---------------------------------------------------------------------------
// Types — mirror the /api/client/inventory responses
// ---------------------------------------------------------------------------

interface CustomsAsset {
  id: string;
  assetClassId: string;
  lifecycleStateId: string;
  skuMatchStatus: string;
  declaredManufacturer: string | null;
  declaredModel: string | null;
  reportedManufacturer: string | null;
  reportedModel: string | null;
  modelDiscrepancy: boolean;
  machineId: string | null;
  deviceCount: number;
  importBatchId: string | null;
  createdAt: string;
  reviewReason: string | null;
  matchedSku: string | null;
  components: CustomsComponent[];
}

interface CustomsComponent {
  id: string;
  assetClassId: string;
  skuMatchStatus: string;
  declaredManufacturer: string | null;
  declaredModel: string | null;
  matchedSku: string | null;
}

interface IngestRow {
  id: string;
  status: string;
  resolvedAssetId: string | null;
  providedAssetId: string | null;
  // No conflictingAssetIds: on a foreign-org conflict those are another
  // organization's asset ids, so the API does not return them here.
  message: string | null;
  createdAt: string;
}

interface TeamOption {
  id: string;
  name: string;
}

function Toast({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 max-w-md rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

function CustomsPage() {
  const { organizationType, user } = useAuth();
  // Customs review is org-admin only (SIL-1898): the API refuses members.
  const isAdmin = user?.role === 'admin';
  const isIndividual = organizationType === 'individual';

  const [assets, setAssets] = useState<CustomsAsset[]>([]);
  const [ingests, setIngests] = useState<IngestRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  // SIL-1898: a failed team load is an error state, not a silently empty select.
  const [teamsFailed, setTeamsFailed] = useState(false);
  // Only the FIRST load blanks the page. A triage or merge refresh keeps the
  // page mounted, so ProbeCommandPanel keeps the one-time token it shows.
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [busyAssetId, setBusyAssetId] = useState<string | null>(null);

  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  const load = useCallback(async () => {
    try {
      // Member-level route (SIL-1898). GET /api/client/teams is org-admin +
      // `teams` feature only, so a member's destination select came back empty.
      const [customsRes, ingestsRes, teamsRes] = await Promise.all([
        isAdmin ? api.api.client.inventory.customs.$get() : null,
        api.api.client.inventory.ingests.$get({ query: {} }),
        isIndividual || !isAdmin ? null : api.api.client.inventory.customs['destination-teams'].$get(),
      ]);
      if (customsRes) throwIfNotOk(customsRes);
      throwIfNotOk(ingestsRes);

      if (customsRes) setAssets((await customsRes.json()).assets as CustomsAsset[]);
      setIngests((await ingestsRes.json()).ingests as IngestRow[]);
      if (teamsRes) {
        if (teamsRes.ok) {
          const body = (await teamsRes.json()) as { teams: TeamOption[] };
          // Customs itself is never a triage destination.
          setTeams(body.teams.filter((t) => t.name !== 'Customs'));
          setTeamsFailed(false);
        } else {
          setTeamsFailed(true);
        }
      }
    } catch {
      setToast({ message: 'Could not load Customs.', type: 'error' });
    } finally {
      setLoaded(true);
    }
  }, [isIndividual, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Grouped by the import batch that brought them in, so a rack triages together. */
  const batches = useMemo(() => {
    const map = new Map<string, CustomsAsset[]>();
    for (const a of assets) {
      const key = a.importBatchId ?? 'ungrouped';
      map.set(key, [...(map.get(key) ?? []), a]);
    }
    return [...map.entries()];
  }, [assets]);

  const conflicts = useMemo(() => ingests.filter((i) => i.status.startsWith('conflict')), [ingests]);

  async function triage(assetId: string, teamId: string | null) {
    setBusyAssetId(assetId);
    try {
      const res = await api.api.client.inventory.customs[':assetId'].triage.$post({
        param: { assetId },
        // Individual orgs have exactly one non-Customs team; the API resolves it (Plan 3).
        json: teamId ? { teamId } : {},
      });
      throwIfNotOk(res);
      setToast({ message: 'Moved out of Customs.', type: 'success' });
      await load();
    } catch {
      setToast({ message: 'Could not move that asset.', type: 'error' });
    } finally {
      setBusyAssetId(null);
    }
  }

  async function matchSku(assetId: string, skuId: string) {
    setBusyAssetId(assetId);
    try {
      const res = await api.api.client.inventory.customs[':assetId']['sku-match'].$post({
        param: { assetId },
        json: { skuId },
      });
      throwIfNotOk(res);
      setToast({ message: 'Model saved. SiliconX will review it.', type: 'success' });
      await load();
    } catch {
      setToast({ message: 'Could not save that model.', type: 'error' });
    } finally {
      setBusyAssetId(null);
    }
  }

  async function merge(assetId: string, targetAssetId: string) {
    setBusyAssetId(assetId);
    try {
      const res = await api.api.client.inventory.customs[':assetId'].merge.$post({
        param: { assetId },
        json: { targetAssetId },
      });
      throwIfNotOk(res);
      setToast({ message: 'Merged into the existing asset.', type: 'success' });
      await load();
    } catch {
      setToast({ message: 'Could not merge — check the target asset id.', type: 'error' });
    } finally {
      setBusyAssetId(null);
    }
  }

  if (!loaded) return <LoadingShell />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customs"
        subtitle="Hardware TrueRead found that could not be matched automatically. Confirm the model of each server and accelerator, then assign it to a team. Nothing here is listed until you do."
      />

      <ProbeCommandPanel notify={notify} />

      {/* --- Conflicts, first, because they are why people come here ----- */}
      {conflicts.length > 0 && (
        <Card>
          <PanelTitle title="Needs a decision" />
          <div className="space-y-2 p-4 pt-0">
            <p className="text-sm text-muted-foreground">
              These readings were saved but not applied — each one disagrees with what is already on file, and
              nothing was changed. You do not need to re-run TrueRead on those machines.
            </p>
            {conflicts.map((c) => (
              <div key={c.id} className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div>
                  <p>{c.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{new Date(c.createdAt).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* --- The holding area ------------------------------------------- */}
      {!isAdmin ? (
        <Card>
          <PanelTitle title="In Customs" />
          <p className="p-4 pt-0 text-sm text-muted-foreground">
            Only organization admins can review and release hardware held in Customs. Ask an admin of your
            organization to confirm the model of each server and accelerator and assign it to a team.
          </p>
        </Card>
      ) : (
      <Card>
        <PanelTitle title="In Customs" />
        <div className="p-4 pt-0">
          {assets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <PackageSearch className="h-8 w-8" />
              <p>Nothing waiting. Hardware TrueRead cannot match to an existing asset lands here.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {batches.map(([batchId, rows]) => (
                <div key={batchId}>
                  <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                    {batchId === 'ungrouped' ? 'Not from an import batch' : `Import batch ${batchId.slice(0, 8)}`} ·{' '}
                    {rows.length} {rows.length === 1 ? 'unit' : 'units'}
                  </p>
                  <div className="space-y-2">
                    {rows.map((a) => (
                      <CustomsRow
                        key={a.id}
                        asset={a}
                        teams={teams}
                        teamsFailed={teamsFailed}
                        canChooseTeam={!isIndividual}
                        busy={busyAssetId === a.id}
                        onTriage={(teamId) => void triage(a.id, teamId)}
                        onMerge={(targetId) => void merge(a.id, targetId)}
                        onMatchSku={(targetAssetId, skuId) => void matchSku(targetAssetId, skuId)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function CustomsRow({
  asset,
  teams,
  teamsFailed,
  canChooseTeam,
  busy,
  onTriage,
  onMerge,
  onMatchSku,
}: {
  asset: CustomsAsset;
  teams: TeamOption[];
  teamsFailed: boolean;
  onMatchSku: (assetId: string, skuId: string) => void;
  canChooseTeam: boolean;
  busy: boolean;
  onTriage: (teamId: string | null) => void;
  onMerge: (targetAssetId: string) => void;
}) {
  const [teamId, setTeamId] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  // Which asset (this server or one of its accelerators) the model picker is open for.
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const reason = asset.skuMatchStatus === 'unmatched' || asset.components.some((c) => c.skuMatchStatus !== 'verified')
    ? customsReasonCopy(toReviewReason(asset.reviewReason), asset.id)
    : null;

  const reported = [asset.reportedManufacturer, asset.reportedModel].filter(Boolean).join(' ');
  const declared = [asset.declaredManufacturer, asset.declaredModel].filter(Boolean).join(' ');

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{reported || declared || 'Unidentified hardware'}</p>
          <p className="text-xs text-muted-foreground">
            {asset.assetClassId} · {asset.deviceCount} {asset.deviceCount === 1 ? 'accelerator' : 'accelerators'} ·{' '}
            {new Date(asset.createdAt).toLocaleDateString()}
          </p>
          {/* The provenance label. This is what a machine said about itself
              through an unprivileged probe, not an attestation, and a buyer's
              diligence should weigh it accordingly. */}
          <p className="mt-1 text-xs text-muted-foreground">Self-reported by the host, not attested</p>

          {asset.modelDiscrepancy && (
            <p className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
              Declared &ldquo;{declared}&rdquo;, hardware reports &ldquo;{reported}&rdquo;. Both are kept; neither
              was overwritten.
            </p>
          )}
          {reason && <p className="mt-1 text-xs text-muted-foreground">{reason.sentence}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {asset.skuMatchStatus === 'verified' ? (
              <span className="text-green-700 dark:text-green-400">Model: {asset.matchedSku ?? 'confirmed'}</span>
            ) : (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => setPickerFor(asset.id)}>
                Pick the model
              </Button>
            )}
          </div>
          {asset.components.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {asset.components.map((ch) => (
                <li key={ch.id} className="flex flex-wrap items-center gap-2">
                  <span>{[ch.declaredManufacturer, ch.declaredModel].filter(Boolean).join(' ') || ch.assetClassId}</span>
                  {ch.skuMatchStatus === 'verified' ? (
                    <span className="text-green-700 dark:text-green-400">{ch.matchedSku ?? 'confirmed'}</span>
                  ) : (
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => setPickerFor(ch.id)}>
                      Pick the model
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canChooseTeam ? (
            <>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                aria-label="Destination team"
              >
                <option value="">Assign to team…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button size="sm" disabled={!teamId || busy} onClick={() => onTriage(teamId)}>
                Assign
              </Button>
              {teamsFailed && (
                <span className="text-xs text-red-600">Could not load your teams. Reload to try again.</span>
              )}
            </>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => onTriage(null)}>
              Move to my assets
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setMergeOpen((v) => !v)}>
            Merge
          </Button>
        </div>
      </div>

      {pickerFor && (
        <CustomsSkuPicker
          assetId={pickerFor}
          busy={busy}
          onPick={(skuId) => {
            onMatchSku(pickerFor, skuId);
            setPickerFor(null);
          }}
          onCancel={() => setPickerFor(null)}
        />
      )}

      {mergeOpen && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="mb-2 text-xs text-muted-foreground">
            If this is an asset you already have on file, merge it: the hardware identity moves onto that asset and
            this placeholder is removed. This is the manual fix for hardware whose serial was not recorded yet.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={mergeTarget}
              onChange={(e) => setMergeTarget(e.target.value)}
              placeholder="Existing asset id"
              className="min-w-[22rem] rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
              aria-label="Target asset id"
            />
            <Button
              size="sm"
              disabled={!mergeTarget || busy}
              onClick={() => {
                if (
                  window.confirm(
                    'Merge permanently removes this Customs placeholder and moves its hardware identity onto the asset you named. This cannot be undone. Continue?',
                  )
                ) {
                  onMerge(mergeTarget);
                }
              }}
            >
              Merge into this asset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
